import { Component, computed, inject, linkedSignal, OnInit } from '@angular/core';
import { LocationsStore } from '../../store/locations.store';
import { LocationDetailsPage } from '../location-details-page/location-details-page';
import {
  NotificationService,
  SimpleDialog,
  InputSimple,
  Select,
  Button,
  SimpleDialogLayout,
} from 'excellence-ui';
import { EnergyInterfaceCreateDialog } from '../../../energy-interfaces/dialogs/energy-interface-create-dialog/energy-interface-create-dialog';
import { EnergyInterfacesCreateSuccessNotification } from '../../../energy-interfaces/notifications/energy-interfaces-create-success-notification/energy-interfaces-create-success-notification';
import { OrganizationsStore } from '../../../organizations/store/organizations.store';
import { NgIcon } from '@ng-icons/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { EnergyInterfaceList } from '../../../energy-interfaces/components/energy-interface-list/energy-interface-list';
import { EnergyInterfacesStore } from '../../../energy-interfaces/stores/energy-interfaces.store';
import { EnergyInterfaceListEntry } from '../../../energy-interfaces/components/energy-interface-list-entry/energy-interface-list-entry';
import { EnergyInterfaceListEmpty } from '../../../energy-interfaces/components/energy-interface-list-empty/energy-interface-list-empty';
import { ActivatedRoute, Router } from '@angular/router';
import { FormField, form } from '@angular/forms/signals';
import { EnergyInterfaceListNoResults } from '../../../energy-interfaces/components/energy-interface-list-no-results/energy-interface-list-no-results';
import { ErrorPage } from '../../../../shared/components/error-page/error-page';
import { EnergyInterfaceListSkeleton } from '../../../energy-interfaces/components/energy-interface-list-skeleton/energy-interface-list-skeleton';

@Component({
  selector: 'app-location-details-energy-interfaces-page',
  templateUrl: './location-details-energy-interfaces-page.html',
  imports: [
    SimpleDialog,
    EnergyInterfaceCreateDialog,
    NgIcon,
    TranslocoPipe,
    EnergyInterfaceList,
    EnergyInterfaceListEntry,
    EnergyInterfaceListEmpty,
    InputSimple,
    FormField,
    Select,
    Button,
    EnergyInterfaceListNoResults,
    ErrorPage,
    SimpleDialogLayout,
    EnergyInterfaceListSkeleton,
  ],
})
export class LocationDetailsEnergyInterfacesPage implements OnInit {
  private organizationsStore = inject(OrganizationsStore);
  private locationsStore = inject(LocationsStore);
  protected energyInterfacesStore = inject(EnergyInterfacesStore);
  private locationDetailsPage = inject(LocationDetailsPage);
  private notificationService = inject(NotificationService);
  private activatedRoute = inject(ActivatedRoute);
  protected router = inject(Router);

  protected locationState = computed(() =>
    this.locationsStore
      .locations()
      ?.find((loc) => loc.id === this.locationDetailsPage.locationId()),
  );
  protected organizationId = this.organizationsStore.selectedId;

  protected energyInterfaces = computed(() => {
    const locationId = this.locationDetailsPage.locationId();
    return (this.energyInterfacesStore.energyInterfaces() ?? [])
      .filter((ei) => !!ei.data && !ei.isDeleted)
      .filter((ei) => ei.data?.locationID === locationId);
  });
  protected filteredEnergyInterfaces = computed(() => {
    const filter = this.filter();
    return this.energyInterfaces()
      .filter(
        (ei) =>
          ei.data?.label.toLowerCase().includes(filter.query?.toLowerCase() ?? '') ||
          ei.data?.maloID?.toLowerCase().includes(filter.query?.toLowerCase() ?? '') ||
          ei.data?.meterID?.toLowerCase().includes(filter.query?.toLowerCase() ?? ''),
      )
      .filter((ei) => (filter.utilityType ? ei.data?.utilityType === filter.utilityType : true));
  });

  protected filter = linkedSignal(() => ({
    query: this.energyInterfacesStore.filterQuery(),
    utilityType: this.energyInterfacesStore.filterUtilityType(),
  }));

  protected filterForm = form(this.filter);

  ngOnInit(): void {
    const utilityType = this.activatedRoute.snapshot.queryParamMap.get('utilityType');
    const query = this.activatedRoute.snapshot.queryParamMap.get('query');
    if (utilityType || query) {
      this.filter.set({ utilityType, query });
      this.onChangeFilter();
    }
  }

  onChangeFilter() {
    const { query, utilityType } = this.filter();
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        query: query,
        utilityType: utilityType,
      },
      queryParamsHandling: 'replace',
    });
  }

  protected onClickResetFilter() {
    this.filter.set({ query: null, utilityType: null });
    this.onChangeFilter();
  }

  protected onCreateSuccess(dialog: SimpleDialog, energyInterfaceId: string) {
    dialog.close();
    const label = this.energyInterfacesStore
      .energyInterfaces()
      ?.find((ei) => ei.id === energyInterfaceId)?.data?.label;
    this.notificationService.show({
      component: EnergyInterfacesCreateSuccessNotification,
      duration: 5000,
      componentProps: {
        organizationId: this.organizationsStore.selectedId(),
        energyInterfaceId,
        energyInterfaceLabel: label,
      },
    });
  }
}
